import React, { useEffect, useState } from "react";
import EditIcon from "@material-ui/icons/Edit";
import ClearIcon from "@material-ui/icons/Clear";
import {
    Box,
    Checkbox,
    IconButton,
    Paper,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { getSortedExceptions, getRuleEditProperties } from "store/features/emailScheduler/definition";
import { Controller, useFormContext } from "react-hook-form";
import { StyledTable } from "components/Table";
import { getEmailSchedulerExceptionsState } from "store/features/emailScheduler/exceptions";
import { getRuleDetailsById } from "store/features/emailScheduler/overview";
import { CreateEmailSchedulerRuleForm, ExceptionCheckbox } from "validation";

interface ExceptionTable {
    onExceptionRemove(id: string): () => void;
    onExceptionEdit(name: string): () => void;
}

export const ExceptionTable: React.FC<ExceptionTable> = ({ onExceptionRemove, onExceptionEdit }) => {
    const { control } = useFormContext<CreateEmailSchedulerRuleForm>();
    const ruleExceptions = useSelector(getSortedExceptions);
    const { isDeleting } = useSelector(getEmailSchedulerExceptionsState);
    const { ruleId } = useSelector(getRuleEditProperties);
    const ruleDetails = useSelector(getRuleDetailsById(ruleId));
    const [selectAll, setSelectAll] = useState(false);

    const setExceptionsInitialState = () => {
        return ruleExceptions.map((exception) => {
            return {
                name: exception,
                isChecked: false,
            };
        });
    };

    const [exceptions, setExceptions] = useState<ExceptionCheckbox[]>(setExceptionsInitialState());

    const updateException = (selectedCheckbox: ExceptionCheckbox, isChecked: boolean) => (
        state: ExceptionCheckbox[]
    ) => {
        return state.map((selectedException) => {
            if (selectedException.name === selectedCheckbox.name) {
                return {
                    name: selectedException.name,
                    isChecked: isChecked,
                };
            }

            return selectedException;
        });
    };

    // eslint-disable-next-line
    const handleSelectedException = (onChange: (...event: any[]) => void, name: string, isChecked: boolean) => {
        // set state for selected exceptions
        setExceptions(updateException({ name, isChecked }, isChecked));
        // update form
        onChange({ name, isChecked });
    };

    const handleToggleAllExceptions = (isChecked: boolean) => {
        const toggledExceptions = exceptions.map((exception) => {
            return {
                ...exception,
                isChecked,
            };
        });

        setExceptions(toggledExceptions);
        control.setValue("general.exceptions", toggledExceptions);
    };

    // if rule is edited, set exceptions state based on rule details
    useEffect(() => {
        if (ruleDetails && ruleDetails.general.exceptions.length) {
            setExceptions((state) =>
                state.map((exception) => {
                    return {
                        name: exception.name,
                        isChecked: ruleDetails.general.exceptions.some(({ name }) => name === exception.name),
                    };
                })
            );
        }
    }, [ruleDetails]);

    // if exceptions were changed (updated/created), set new exceptions state
    useEffect(() => {
        setExceptions((state) => {
            return ruleExceptions.map((exceptionName) => {
                return {
                    name: exceptionName,
                    isChecked: state.some(({ name, isChecked }) => name === exceptionName && isChecked),
                };
            });
        });
    }, [ruleExceptions]);

    // sync exceptions form state for every exception change
    useEffect(() => {
        control.setValue("general.exceptions", exceptions);
        // eslint-disable-next-line
    }, [exceptions]);

    return (
        <Box pt={1} mb={4} flex={0.9}>
            <TableContainer component={Paper}>
                <StyledTable>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={false}
                                    checked={selectAll}
                                    onChange={(e, isChecked) => {
                                        setSelectAll(isChecked);
                                        handleToggleAllExceptions(isChecked);
                                    }}
                                />
                            </TableCell>
                            <TableCell align="left" padding="default">
                                Name
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {exceptions.map((exception, index) => {
                            return (
                                <TableRow key={`${exception}_${index}`} role="checkbox">
                                    <TableCell padding="checkbox">
                                        <Controller
                                            name={`general.exceptions[${index}]`}
                                            type="checkbox"
                                            control={control}
                                            defaultValue={false}
                                            render={({ onChange }) => {
                                                return (
                                                    <Checkbox
                                                        checked={exception.isChecked}
                                                        onChange={(event, isChecked) =>
                                                            handleSelectedException(onChange, exception.name, isChecked)
                                                        }
                                                    />
                                                );
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="left">{exception.name}</TableCell>
                                    <TableCell align="right" padding="none">
                                        <IconButton onClick={onExceptionEdit(exception.name)} disabled={isDeleting}>
                                            <EditIcon fontSize="small" color={isDeleting ? "disabled" : "primary"} />
                                        </IconButton>

                                        <IconButton onClick={onExceptionRemove(exception.name)} disabled={isDeleting}>
                                            <ClearIcon fontSize="small" color={isDeleting ? "disabled" : "error"} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </Box>
    );
};
